import { Injectable, inject, computed } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import { filter, map, take } from "rxjs/operators";

import { Guest, User } from "@hermes/api-model-account";

import {
    clearUserData,
    fetchGuest,
    removeGuest,
    setUser,
    updateGuest,
} from "../actions/user.actions";
import { UserState } from "../reducers/user.state";
import * as userQuery from "../selectors/user.selectors";

@Injectable()
export class UserFacade {
    private store = inject<Store<UserState>>(Store);

    // User Selectors
    public userDetails$ = this.store.select(userQuery.selectUserDetails);

    public readonly userDetails = this.store.selectSignal(
        userQuery.selectUserDetails,
    );

    public customerId$ = this.store.select(userQuery.selectCustomerId);

    public isLoggedIn$ = this.store.select(userQuery.selectIsLoggedIn);

    public readonly isLoggedIn = this.store.selectSignal(
        userQuery.selectIsLoggedIn,
    );

    public userPrefix$ = this.store.select(userQuery.selectUserPrefix);

    public userType$ = this.store.select(userQuery.selectUserType);

    public userStatus$ = this.store.select(userQuery.selectUserStatus);

    public userIsSynchronized$ = this.userStatus$.pipe(
        map((status) => status === "synchronized"),
    );

    public userIsSynchronized = toSignal(this.userIsSynchronized$);

    // Guest Selectors
    public guestData$ = this.store.select(userQuery.selectGuestData);

    public readonly guestData = this.store.selectSignal(
        userQuery.selectGuestData,
    );

    public guestDob$ = this.store.select(userQuery.selectGuestDob);

    public guestNewsletter$ = this.store.select(
        userQuery.selectGuestNewsletter,
    );

    public isGuestConnected$ = this.store.select(
        userQuery.selectIsGuestConnected,
    );

    public readonly isGuestConnected = this.store.selectSignal(
        userQuery.selectIsGuestConnected,
    );

    public readonly fetchUserInfosCompleted = computed(() => {
        const customerData = {
            userData: this.userDetails(),
            guestData: this.guestData(),
        };
        return (
            !!(
                customerData.userData &&
                (customerData.userData.email ||
                    customerData.userData.phoneLogin)
            ) ||
            !!(
                customerData.guestData &&
                (customerData.guestData.email ||
                    customerData.guestData.phoneLogin)
            )
        );
    });

    // Actions
    public fetchGuest(): void {
        this.store.dispatch(fetchGuest());
    }

    public updateGuest(guest: Guest): void {
        this.store.dispatch(updateGuest({ guest }));
    }

    public setUser(user: User): void {
        this.store.dispatch(setUser({ user }));
    }

    public removeGuest(): void {
        this.store.dispatch(removeGuest());
    }

    public isConnected(): Observable<{
        userConnected: boolean;
        guestConnected: boolean;
    }> {
        return combineLatest([this.isLoggedIn$, this.isGuestConnected$]).pipe(
            filter(
                ([user, guest]) => user !== undefined && guest !== undefined,
            ),
            take(1),
            map(([user, guest]) => ({
                userConnected: user,
                guestConnected: guest,
            })),
        );
    }

    public clearUserData(): void {
        this.store.dispatch(clearUserData());
    }
}
